<template>
  <div v-redirect-if-no-permission:MANAGE_FINANCE_ADJUSTMENTS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <v-card class="mb-2">
      <div class="fin-header">
        <div class="fin-toolbar">
          <span class="fin-title">{{ $t("finance.adjustments.captainAdjustments") }}</span>
        </div>
        <div class="fin-filters">
          <choose-captains-multi
            :multi="false"
            :label="$t('finance.common.captain')"
            v-model="form.captain_id"
            class="fin-field--wide"
          />
          <v-select
            :items="adjustmentTypes"
            item-text="text"
            item-value="value"
            v-model="form.adjustment_type"
            :label="$t('finance.adjustments.adjustmentType')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-text-field
            v-model.number="form.amount"
            :label="$t('finance.common.amount')"
            type="number"
            hide-details
            class="fin-field"
          ></v-text-field>
          <v-text-field
            v-model="form.posted_at"
            :label="$t('finance.adjustments.postingDate')"
            type="date"
            hide-details
            class="fin-field"
          ></v-text-field>
          <v-text-field
            v-model="form.note"
            :label="$t('finance.common.note')"
            hide-details
            class="fin-field--wide"
          ></v-text-field>
          <v-btn small color="primary" :loading="saving" @click="submitAdjustment">{{ $t("finance.adjustments.postAdjustment") }}</v-btn>
        </div>
      </div>
    </v-card>

    <v-card>
      <div class="fin-header">
        <div class="fin-toolbar">
          <span class="fin-title">{{ $t("finance.adjustments.recentAdjustmentJournals") }}</span>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="filterAccountCode"
            :label="$t('finance.adjustments.accountCodeSearch')"
            clearable
            hide-details
            maxlength="20"
            class="fin-field"
            @keyup.enter="loadEntries"
          ></v-text-field>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="loadEntries">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ $t("finance.common.refresh") }}</span>
          </v-tooltip>
        </div>
      </div>
      <v-data-table
        class="fin-dt"
        :headers="headers"
        :items="entries"
        :rows-per-page-items="[10, 25, 50]"
      >
        <template v-slot:items="props">
          <tr>
            <td>{{ props.item.id }}</td>
            <td>{{ formatDate(props.item.journal_date) }}</td>
            <td>{{ props.item.source }}</td>
            <td>{{ props.item.captain ? (props.item.captain.first_name + ' ' + props.item.captain.last_name) : '-' }}</td>
            <td>{{ lineAccountCodes(props.item) }}</td>
            <td>{{ props.item.memo || '-' }}</td>
            <td>{{ props.item.created_by_name || $t("finance.common.performedBySystem") }}</td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { formatFinanceDate } from "@/mixins/financeDate";
import ChooseCaptainsMulti from "@/components/monaco/input/ChooseCaptainMulti";
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";

export default {
  name: "CaptainAdjustments",
  title() {
    return this.$t("finance.adjustments.pageTitle");
  },
  components: { ChooseCaptainsMulti, FinanceSectionNav, LoadingComponent },
  data() {
    return {
      loading: false,
      saving: false,
      entries: [],
      filterAccountCode: null,
      form: {
        captain_id: null,
        adjustment_type: "bonus",
        amount: null,
        posted_at: null,
        note: null,
      },
    };
  },
  computed: {
    adjustmentTypes() {
      return [
        { text: this.$t("finance.common.bonus"), value: "bonus" },
        { text: this.$t("finance.common.penalty"), value: "penalty" },
        { text: this.$t("finance.common.hrDeduction"), value: "hr_deduction" },
      ];
    },
    headers() {
      return [
        { text: this.$t("finance.common.id"), value: "id" },
        { text: this.$t("finance.common.date"), value: "journal_date" },
        { text: this.$t("finance.common.source"), value: "source" },
        // Item 4.2: the cell renders an OBJECT, so sorting by it would order
        // rows by nothing at all — the driver column sorts by name instead.
        { text: this.$t("finance.common.captain"), value: "captain.first_name" },
        { text: this.$t("finance.adjustments.lineAccounts"), value: "lines", sortable: false },
        { text: this.$t("finance.common.memo"), value: "memo" },
        // Item 4.1: the employee who performed the adjustment.
        { text: this.$t("finance.common.performedBy"), value: "created_by_name" },
      ];
    },
  },
  mounted() {
    this.loadEntries();
  },
  methods: {
    getErrorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return fallback;
    },
    submitAdjustment() {
      if (!this.form.captain_id || !this.form.amount) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.adjustments.captainAmountRequired"), "red");
        return;
      }

      this.saving = true;
      var payload = Object.assign({}, this.form, {
        amount: this.toStoredAmount(this.form.amount),
      });
      this.axios
        .post(this.api.ACTIONS.ACCOUNTING_ADJUSTMENTS, payload)
        .then(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.adjustments.adjustmentPosted"), "green");
            this.form.amount = null;
            this.form.note = null;
            this.loadEntries();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.adjustments.failedPost")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.saving = false;
          }.bind(this)
        );
    },
    lineAccountCodes(entry) {
      var codes = (entry.lines || [])
        .map(function (line) {
          if (line.account && line.account.code) return line.account.code;
          return line.account_code || null;
        })
        .filter(function (code) {
          return !!code;
        });
      return codes.length ? codes.join(", ") : "-";
    },
    loadEntries() {
      this.loading = true;
      var params = {
        perPage: 100,
        source: "adjustment",
      };
      // Backend validates account_code as string|max:20 — clamp client-side
      // so a long paste can never 422 the whole list load.
      var accountCode = (this.filterAccountCode || "").trim().slice(0, 20);
      if (accountCode) {
        params.account_code = accountCode;
      }
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_JOURNAL_ENTRIES, {
          params: params,
        })
        .then(
          function (response) {
            this.entries = response.data.data.data || [];
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.adjustments.failedLoad")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    formatDate(value) {
      // One shared finance formatter (accountant doc 2026-10-03 item 1.8).
      return formatFinanceDate(value);
    },
  },
};
</script>